// campaign-editability.ts — what may still be edited on a campaign (#103).
//
// Keyed on REAL dispatch state, NOT on whether a campaign document exists. The
// old gate (`!d.campaignId` in sequence-detail) locked a campaign as soon as its
// document was created, which happens at LAUNCH — so a campaign scheduled for
// next week was already locked despite nothing having been sent.

/** `full` = audience/timing/template. `template` = message + attachment only. */
export type CampaignEditability = 'full' | 'template' | 'none';

/** Terminal states — nothing further will send, so nothing is editable.
 *  `complete-send` (no trailing "t") is a legacy spelling still widely persisted;
 *  matches the set in management-list.tsx `campaignIsDispatched`.
 *  `complete-with-skips` (BE#154) is terminal too: it dispatched everything it
 *  COULD, dropping only recipients with no resolvable WhatsApp ID. Leaving it
 *  out did not change which editor opens (an unknown status refuses as well),
 *  but it made `isTerminalCampaignStatus` answer false — so a campaign that had
 *  finished was described as still live and offered an "Edit message" remedy
 *  pointing at an editor guaranteed to refuse it. */
const TERMINAL = new Set([
    'completed', 'complete-sent', 'complete-delivered', 'complete-send',
    'complete-with-skips', 'aborted',
]);

/** Nothing further will send. Exported for the tier-2 editor's outcome copy:
 *  the server's `campaignResumed` is `campaignStatus === 'running'`, i.e. plain
 *  STATE, so `false` covers "still paused" AND "it finished while you edited".
 *  Those need different sentences. */
export const isTerminalCampaignStatus = (status?: string | null): boolean =>
    Boolean(status) && TERMINAL.has(status as string);

/** Launched but not yet dispatching. Redis claims are seeded only on the
 *  pending -> running transition, so there is no schedule to desync yet. */
const NOT_STARTED = new Set(['pending']);

/** Currently dispatching (or pause-able mid-dispatch). */
const IN_FLIGHT = new Set(['running', 'active', 'paused']);

export const campaignEditability = (status?: string): CampaignEditability => {
    if (!status) return 'none';
    if (TERMINAL.has(status)) return 'none';
    if (NOT_STARTED.has(status)) return 'full';
    if (IN_FLIGHT.has(status)) return 'template';
    // Unknown status: refuse rather than guess. A new status that should be
    // editable must be added deliberately.
    return 'none';
};

/** Which menu action a campaign's status earns, or null for none.
 *  Drafts are NOT handled here — the wizard's existing draft `edit` path owns them.
 *
 *  DO NOT ROUTE A MENU WITH THIS. It answers the TIER (`edit` = tier 1,
 *  `edit-content` = tier 2), not the destination, and `edit` was read as "open
 *  the wizard" — which on a launched drip re-launches it and double-sends the
 *  whole audience, and on a standalone campaign opens a wizard that can never
 *  hydrate. Route with `menuEditTargetFor` / `standaloneEditTargetFor` /
 *  `stepEditTargetFor` below, each of which names a real destination. Kept as
 *  the shared tier vocabulary those three are checked against. */
export const editActionKeyFor = (status?: string): 'edit' | 'edit-content' | null => {
    switch (campaignEditability(status)) {
        case 'full': return 'edit';
        case 'template': return 'edit-content';
        default: return null;
    }
};

/** Which editor a drip step's in-flight edit affordances should reach. This is
 *  the TIER split, and it mirrors `editActionKeyFor` — `full` is tier 1 and
 *  `template` is tier 2 — so the step row and the header menu never send the
 *  same campaign to two different editors.
 *
 *  `step-editor`     — tier 1. Either no campaign document exists yet (the step
 *                      is still a plan on `drip.steps`), or one exists but is
 *                      `pending`: nothing is dispatching, so the drip step is
 *                      edited DIRECTLY through `DripStepEditModal` and the
 *                      backend's `syncPendingStepCampaignOnPatch` after-patch
 *                      hook on `drips` propagates the new template onto the
 *                      pending step campaign.
 *  `campaign-editor` — tier 2. The campaign is dispatching (or paused
 *                      mid-dispatch), so the edit must go through the server's
 *                      pause/commit/resume cycle (`CampaignContentEditModal`).
 *  `locked`          — terminal: nothing further will send, nothing to edit. */
export type StepEditTarget = 'step-editor' | 'campaign-editor' | 'locked';

/** TWO cases here are easy to get wrong, and both were:
 *
 *  1. The campaign-ABSENT case must NOT fall through to `campaignEditability`.
 *     That function is status-driven and answers `none` for an absent status —
 *     correct for a campaign, wrong for a step that has no campaign at all.
 *     Routing on it directly would LOCK every unlaunched follow-up.
 *  2. `full` must NOT collapse into `campaign-editor`. The backend's
 *     `EDITABLE_CAMPAIGN_STATUSES` is `['running','active','paused']` and
 *     `beginCampaignEdit` throws `BadRequest` on a `pending` campaign ("a
 *     pending one is edited directly (tier 1)"). Sending `pending` to the
 *     campaign editor produces an affordance that opens, round-trips, and
 *     reports "this campaign can't be edited right now" — on precisely the
 *     case #103 exists to unlock. */
export const stepEditTargetFor = (campaignId?: string, campaignStatus?: string): StepEditTarget => {
    if (!campaignId) return 'step-editor';
    switch (campaignEditability(campaignStatus)) {
        case 'full': return 'step-editor';
        case 'template': return 'campaign-editor';
        default: return 'locked';
    }
};

/** Which editor the SEQUENCE OVERFLOW MENU's edit item opens, or null for no
 *  edit item at all.
 *
 *  FE#332 — EDIT NOW REOPENS THE FULL WIZARD, prepopulated, for EVERY
 *  non-terminal drip (draft, scheduled, active, paused) — not the content-only
 *  editors the redesign had regressed it to. Restoring this is the whole fix:
 *  the customer must be able to go back to ANY step (audience/groups, schedule,
 *  recurrence, text/image), not just change text+image.
 *
 *  THIS IS SAFE — the reason the wizard USED to be fenced off is gone. Its
 *  completion button (`handleLaunch`) no longer relaunches a launched drip: for
 *  an already-launched drip it now SAVES IN PLACE (`buildDripEditPatch` →
 *  `DBDripThunks.patch`, never `launchDrip`/`advanceDrip`), so it can't mint a
 *  second step-0 campaign over the audience. The backend
 *  `syncPendingStepCampaignOnPatch` hook fans the edits onto the PENDING step
 *  campaigns and is guarded to skip any running/complete step (frozen into Redis
 *  at run-start), so a running send is never mutated regardless. See
 *  `wizard-edit-save.ts` + `dripHasLaunched`.
 *
 *  Only a TERMINAL drip (completed/aborted) has nothing left to edit → null. The
 *  per-step inline Edit button still uses `stepEditTargetFor` for a scoped
 *  single-step edit; this menu item is the "edit the whole flow" entry. */
export type MenuEditTarget = 'wizard' | 'step-editor' | 'campaign-editor' | null;

export const menuEditTargetFor = (
    dripStatus?: string,
    // Kept for signature compatibility with the per-step decision + callers; the
    // whole-flow edit no longer branches on the current step's campaign status
    // (the wizard opens read-only-gated internally instead).
    _currentStepCampaignStatus?: string,
): MenuEditTarget => {
    // A terminal drip is done — nothing to edit.
    if (dripStatus === 'completed' || dripStatus === 'aborted') return null;
    // Everything else — draft, scheduled/pending, active, paused — reopens the
    // full wizard. Save-in-place makes this double-send-safe by construction.
    return 'wizard';
};

/** Has this drip already been LAUNCHED? The wizard's completion button
 *  (`handleLaunch`) must refuse on `true`: `launchDrip` mints a fresh step-0
 *  campaign over the whole audience every time it is called, so a second call
 *  on a launched drip messages every recipient twice.
 *
 *  `menuEditTargetFor` keeps the MENU off that path, but the wizard route
 *  (`/campaigns/:id/edit/:step`) is reachable by typed URL, by a stale tab, and
 *  by browser history — and reproducing it by URL on a live drip really did
 *  mint a second step-0 campaign over the same 40-contact audience. The guard
 *  therefore belongs at the point of damage too, not only at the entrance.
 *
 *  TWO signals, because either alone has a hole:
 *   - `status`: a launched drip is `active`/`paused`/`completed`/`aborted`.
 *     An ABSENT status must NOT count as launched — a freshly created draft is
 *     written `status:'draft'`, but a drip that somehow lost the field is still
 *     a draft that must be launchable, and refusing there would brick the
 *     genuine first launch (the only flow that creates campaigns at all).
 *   - `startedAt`: stamped by the server at launch and never cleared. It is the
 *     signal that survives the drift window this very file's `handleLaunch`
 *     used to open — patch `status:'draft'`, then launch — where a failure or
 *     a lost response between the two calls leaves a LAUNCHED drip persisted as
 *     a draft. Status alone would wave that drip straight back into a relaunch.
 */
export const dripHasLaunched = (status?: string | null, startedAt?: unknown): boolean =>
    (Boolean(status) && status !== 'draft') || Boolean(startedAt);

/** Which editor a STANDALONE (drip-less) campaign's menu may open.
 *
 *  `schedule-editor`  — tier 1, `ScheduledCampaignEditModal`. The campaign is
 *                       `pending`: launched, but the dispatcher has not started
 *                       it, so the message AND the audience AND the send time
 *                       are all still changeable.
 *  `campaign-editor`  — tier 2, `CampaignContentEditModal`. It is dispatching,
 *                       so only the message can change (the audience and every
 *                       per-contact send time were frozen into Redis by
 *                       `initCampaignInRedis` on the pending -> running
 *                       transition).
 *  `null`             — terminal, or a status we do not recognise.
 *
 *  THE WIZARD IS STILL FENCED OFF, and that is why this function exists rather
 *  than reusing `menuEditTargetFor`. The wizard hydrates by DRIP id
 *  (`wizard.tsx`: `dripsSelectOneObjectByQuery(..., {_id: dripId})`), so opening
 *  it on a standalone campaign id hydrates nothing, `hydratedForThisDrip` never
 *  flips, and it renders "Loading…" forever. Tier 1 for a standalone campaign
 *  used to be simply ABSENT for that reason — which is the half of #103 the
 *  customer actually hit: "it seems you can only open drafts for editing …
 *  I can't update or change [my scheduled campaigns]". `schedule-editor` is the
 *  purpose-built tier-1 editor that closes it; it is NOT the wizard, and it must
 *  never be routed to one. */
export type StandaloneEditTarget = 'schedule-editor' | 'campaign-editor' | null;

export const standaloneEditTargetFor = (campaignStatus?: string): StandaloneEditTarget => {
    switch (campaignEditability(campaignStatus)) {
        case 'full': return 'schedule-editor';
        case 'template': return 'campaign-editor';
        default: return null;
    }
};

/** What the edit route `/campaigns/:id/edit/:step` must render for a given id
 *  (FE#214). The route used to resolve to the drip wizard UNCONDITIONALLY, but
 *  the wizard hydrates by DRIP id (`dripsSelectOneObjectByQuery({_id})`), so a
 *  STANDALONE campaign id never hydrated and the route hung on "Loading…" forever
 *  — the customer's "no way to edit my scheduled campaign".
 *
 *  Resolve it by identity + tier instead:
 *   - a DRIP id → `wizard` (the drip-sequence editor it is built for);
 *   - a standalone CAMPAIGN id → the purpose-built, SERVER-AUTHORITATIVE modal for
 *     its editability tier (`schedule-editor` tier-1 / `campaign-editor` tier-2),
 *     the SAME editors the menu already routes standalone campaigns to — so the
 *     route and the 3-dot menu are consistent about what is editable;
 *   - a terminal/unknown campaign, or an id that resolves to neither → `none`
 *     (the route shows a graceful "can't edit" notice, never an infinite spinner). */
export type EditRouteSurface = 'wizard' | 'schedule-editor' | 'campaign-editor' | 'none';

export const resolveCampaignEditSurface = (opts: {
    isDrip: boolean;
    campaignStatus?: string;
}): EditRouteSurface => {
    if (opts.isDrip) return 'wizard';
    return standaloneEditTargetFor(opts.campaignStatus) ?? 'none';
};

// NOTE: `pauseCycleTransitions` used to live here — the client-side decision of
// which status to write on open and on close. It is gone deliberately. The
// pause/resume cycle is now owned end to end by the server actions
// (`campaign-edit-begin` / `-commit` / `-abort`), which perform each transition
// as an atomic guarded write and hand the client a definitive outcome. A client
// copy of that decision could only ever disagree with the server about what
// happened — which is what the earlier client-orchestrated version did.
